function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { useMemoizedValue } from "@sail/react";
import { noop } from "@sail/utils";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { getApolloContext, useDataContext } from "../apollo/operationContext";
import useGraphQlDataClient from "../config/useGraphQlDataClient";
import calculateSubQueryRuntimeValues from "./compositionFragments/calculateSubQueryRuntimeValues";
import { ROOT_QUERY_INCLUDE_VARIABLE_NAME } from "./compositionFragments/constants";
import splitComposedQueryDocIntoSubQueries from "./compositionFragments/splitComposedQueryDocIntoSubQueries";
import transformCompositionFragmentToQuery from "./compositionFragments/transformCompositionFragmentToQuery";
import { findEntryPointQuery } from "./documentUtils/entrypoint";
import { getOperationName } from "./documentUtils/query";
import sanitizeComposedErrors from "./sanitizeComposedErrors";
import calculateSubQueryIncludes from "../queryUtils/calculateSubQueryIncludes";
import dedupeSubQueries from "../queryUtils/dedupeSubQueries";
import { extractFieldNameFromAlias, extractPrefixFromVariableName } from "../queryUtils/prefixes";
import { buildQueryAndVariablesHash } from "../queryUtils/queryAndVariablesHashing";
class RootQueriesInFlight {
    add(rootQueryHash, apolloSubscription) {
        this.map.set(rootQueryHash, {
            unsubscribe: apolloSubscription.unsubscribe,
            count: 1
        });
    }
    decrement(rootQueryHash) {
        const rootQueryInFlight = this.map.get(rootQueryHash);
        if (rootQueryInFlight) {
            rootQueryInFlight.count--;
            if (rootQueryInFlight.count === 0) {
                rootQueryInFlight.unsubscribe();
                this.delete(rootQueryHash);
            }
        }
    }
    increment(rootQueryHash) {
        const rootQueryInFlight = this.map.get(rootQueryHash);
        if (rootQueryInFlight) {
            rootQueryInFlight.count++;
            return rootQueryInFlight;
        }
        return null;
    }
    delete(rootQueryHash) {
        this.map.delete(rootQueryHash);
    }
    has(rootQueryHash) {
        return this.map.has(rootQueryHash);
    }
    size() {
        return this.map.size;
    }
    clear() {
        this.map.clear();
    }
    constructor(){
        _define_property(this, "map", new Map());
    }
}
export const rootQueriesInFlight = new RootQueriesInFlight();
function buildRootQueryFetchParams({ dedupedSubQueryGroups, forcedFetchFragments, memoryCache, resultStore, deduplicatedIncludeVariables, rootQueryInstanceId, variables, transformedQuery }) {
    const rootQueryIncludeVariables = calculateSubQueryIncludes(dedupedSubQueryGroups, forcedFetchFragments, memoryCache, resultStore, deduplicatedIncludeVariables, rootQueryInstanceId);
    const variablesWithIncludes = _object_spread({}, variables, rootQueryIncludeVariables);
    const isAnyFragmentIncluded = Object.values(rootQueryIncludeVariables).some((value)=>value === true);
    const rootQueryHash = buildQueryAndVariablesHash(transformedQuery, variablesWithIncludes).documentHashWithVariables;
    const subQueriesWithIncludeTrue = dedupedSubQueryGroups.filter(function filterSubQueriesWithIncludeTrue(subQueryGroup) {
        const includeVariableName = `${subQueryGroup.prefix}___${ROOT_QUERY_INCLUDE_VARIABLE_NAME}`;
        return rootQueryIncludeVariables[includeVariableName] === true;
    });
    return {
        rootQueryHash,
        variablesWithIncludes,
        subQueriesWithIncludeTrue,
        isAnyFragmentIncluded
    };
}
function useTransformedRootQuery(compositionFragment, options, observableStore) {
    const rootQueryInstanceId = useMemo(()=>buildQueryAndVariablesHash(compositionFragment, options === null || options === void 0 ? void 0 : options.variables).documentHashWithVariables, [
        compositionFragment,
        options === null || options === void 0 ? void 0 : options.variables
    ]);
    const { query: transformedQuery, prefixToFragmentName } = useMemo(()=>transformCompositionFragmentToQuery(compositionFragment), [
        compositionFragment
    ]);
    const component = useMemo(()=>{
        const queryDefinition = findEntryPointQuery(transformedQuery);
        const operationName = getOperationName(queryDefinition);
        return operationName.replace(/Query$/, '');
    }, [
        transformedQuery
    ]);
    const subQueryGraphQlDocuments = useMemo(()=>{
        return splitComposedQueryDocIntoSubQueries(transformedQuery);
    }, [
        transformedQuery
    ]);
    const rawSubQueryGroups = useMemo(()=>{
        return calculateSubQueryRuntimeValues(subQueryGraphQlDocuments, options, observableStore);
    }, [
        subQueryGraphQlDocuments,
        options,
        observableStore
    ]);
    const { dedupedSubQueryGroups, deduplicatedIncludeVariables } = useMemo(()=>dedupeSubQueries(rawSubQueryGroups), [
        rawSubQueryGroups
    ]);
    return {
        rootQueryInstanceId,
        transformedQuery,
        prefixToFragmentName,
        component,
        dedupedSubQueryGroups,
        deduplicatedIncludeVariables
    };
}
export default function useRootQuery(compositionFragment, options) {
    const { apolloLinkExecutor, resultStore, memoryCache, observableStore } = useGraphQlDataClient();
    const { rootQueryInstanceId, transformedQuery, prefixToFragmentName, component, dedupedSubQueryGroups, deduplicatedIncludeVariables } = useTransformedRootQuery(compositionFragment, options, observableStore);
    const dataContext = useDataContext();
    const createApolloLinkContext = useCallback((expectedErrors, includedPrefixes)=>{
        const includedOperationNames = Object.fromEntries(includedPrefixes.map((prefix)=>[
                prefix,
                prefixToFragmentName[prefix]
            ]));
        return getApolloContext(dataContext, {
            type: 'COMPOSITION_QUERY',
            operationNames: includedOperationNames,
            expectedErrors
        }, component);
    }, [
        dataContext,
        prefixToFragmentName,
        component
    ]);
    const stableExpectedErrors = useMemoizedValue(options.expectedErrors || {});
    const getFetchParams = useCallback((forcedFetchFragments = [])=>buildRootQueryFetchParams({
            dedupedSubQueryGroups,
            forcedFetchFragments,
            memoryCache,
            resultStore,
            deduplicatedIncludeVariables,
            rootQueryInstanceId,
            variables: options.variables,
            transformedQuery
        }), [
        dedupedSubQueryGroups,
        memoryCache,
        resultStore,
        deduplicatedIncludeVariables,
        options,
        transformedQuery,
        rootQueryInstanceId
    ]);
    const executeFetch = useCallback(({ rootQueryHash, variablesWithIncludes, subQueriesWithIncludeTrue, isAnyFragmentIncluded })=>{
        if (!isAnyFragmentIncluded) {
            return null;
        }
        const includedPrefixes = subQueriesWithIncludeTrue.map((includedSubQuery)=>includedSubQuery.prefix);
        const apolloLinkContext = createApolloLinkContext(stableExpectedErrors, includedPrefixes);
        const rootQueryInFlight = rootQueriesInFlight.increment(rootQueryHash);
        if (!rootQueryInFlight) {
            const apolloSubscription = apolloLinkExecutor.execute(transformedQuery, variablesWithIncludes, apolloLinkContext, {
                next: (result)=>{
                    var _result_errors_networkErrors, _result_errors, _result_errors_clientErrors, _result_errors1;
                    rootQueriesInFlight.delete(rootQueryHash);
                    settleResultStore(result, resultStore, dedupedSubQueryGroups);
                    const succeeded = !((_result_errors = result.errors) === null || _result_errors === void 0 ? void 0 : (_result_errors_networkErrors = _result_errors.networkErrors) === null || _result_errors_networkErrors === void 0 ? void 0 : _result_errors_networkErrors.length) && !((_result_errors1 = result.errors) === null || _result_errors1 === void 0 ? void 0 : (_result_errors_clientErrors = _result_errors1.clientErrors) === null || _result_errors_clientErrors === void 0 ? void 0 : _result_errors_clientErrors.length);
                    subQueriesWithIncludeTrue.forEach((subQueryGroup)=>{
                        subQueryGroup.observableFragment.setLastFetchedBy(rootQueryInstanceId, succeeded);
                        subQueryGroup.observableFragment.setIsPending(false);
                    });
                },
                complete: noop
            });
            rootQueriesInFlight.add(rootQueryHash, apolloSubscription);
        }
        return {
            subQueriesWithIncludeTrue,
            rootQueryHash
        };
    }, [
        apolloLinkExecutor,
        createApolloLinkContext,
        dedupedSubQueryGroups,
        stableExpectedErrors,
        resultStore,
        transformedQuery,
        rootQueryInstanceId
    ]);
    const setSubQueriesPending = useCallback((fetchResult)=>{
        const { subQueriesWithIncludeTrue, rootQueryHash } = fetchResult;
        if (!rootQueriesInFlight.has(rootQueryHash)) {
            return;
        }
        subQueriesWithIncludeTrue.forEach((subQueryGroup)=>{
            subQueryGroup.observableFragment.setIsPending(true);
        });
    }, []);
    const refetchFragments = useCallback((fragments)=>{
        const params = getFetchParams(fragments);
        const fetchResult = executeFetch(params);
        if (!fetchResult) {
            return;
        }
        setSubQueriesPending(fetchResult);
    }, [
        executeFetch,
        getFetchParams,
        setSubQueriesPending
    ]);
    useEffect(()=>{
        observableStore.addRootQueryRefetchData(transformedQuery, refetchFragments, dedupedSubQueryGroups.map(({ fragmentHash })=>fragmentHash));
        return ()=>{
            observableStore.removeRootQueryRefetchData(transformedQuery);
        };
    }, [
        observableStore,
        refetchFragments,
        transformedQuery,
        dedupedSubQueryGroups
    ]);
    const renderFetchParams = useMemo(()=>getFetchParams(), [
        getFetchParams
    ]);
    const fetchResult = useMemo(()=>{
        return executeFetch(renderFetchParams);
    }, [
        renderFetchParams,
        executeFetch
    ]);
    const rootQueryHashRef = useRef(null);
    useEffect(()=>{
        // Cancel previous requests if needed
        const newHash = renderFetchParams.rootQueryHash;
        const oldHash = rootQueryHashRef.current;
        if (oldHash && oldHash !== newHash) {
            rootQueriesInFlight.decrement(oldHash);
        }
        rootQueryHashRef.current = newHash;
    }, [
        renderFetchParams.rootQueryHash
    ]);
    useEffect(()=>{
        if (!fetchResult) {
            return;
        }
        setSubQueriesPending(fetchResult);
    }, [
        fetchResult,
        setSubQueriesPending
    ]);
    useEffect(()=>{
        return ()=>{
            const rootQueryHash = rootQueryHashRef.current;
            if (!rootQueryHash) {
                return;
            }
            rootQueriesInFlight.decrement(rootQueryHash);
        };
    }, []);
}
export function parseAndGroupResults(result) {
    const subResults = {};
    const errors = result.errors;
    Object.entries(result.data || {}).forEach(([alias, value])=>{
        const prefix = extractPrefixFromVariableName(alias);
        const fieldName = extractFieldNameFromAlias(alias);
        if (!subResults[prefix]) {
            subResults[prefix] = {
                prefix,
                data: {},
                errors: null
            };
        }
        subResults[prefix].data[fieldName] = value;
    });
    errors === null || errors === void 0 ? void 0 : errors.graphQlErrors.forEach((error)=>{
        const path = error.path;
        if (path && path.length > 0 && typeof path[0] === 'string') {
            const key = path[0];
            const prefix = extractPrefixFromVariableName(key);
            if (!subResults[prefix]) {
                subResults[prefix] = {
                    prefix,
                    data: null,
                    errors: {
                        graphQlErrors: [],
                        clientErrors: [],
                        networkErrors: []
                    }
                };
            } else if (!subResults[prefix].errors) {
                subResults[prefix].errors = {
                    graphQlErrors: [],
                    clientErrors: [],
                    networkErrors: []
                };
            }
            subResults[prefix].errors.graphQlErrors.push(error);
        }
    });
    return Object.values(subResults);
}
export function settleResultStore(result, resultStore, subQueryGroupsWithMetadata) {
    var _result_errors, _result_errors1;
    const subQueryGroupsWithoutSkip = subQueryGroupsWithMetadata.filter((subQueryGroup)=>subQueryGroup.skip !== true);
    const hasNetworkOrClientErrors = ((_result_errors = result.errors) === null || _result_errors === void 0 ? void 0 : _result_errors.networkErrors.length) || ((_result_errors1 = result.errors) === null || _result_errors1 === void 0 ? void 0 : _result_errors1.clientErrors.length);
    if (hasNetworkOrClientErrors) {
        subQueryGroupsWithoutSkip.forEach((subQueryWithMetadata)=>{
            const { subQuery, variables } = subQueryWithMetadata;
            resultStore.setFetchingResults(subQuery, variables, result);
        });
        return;
    }
    const groupedByPrefix = parseAndGroupResults(result);
    groupedByPrefix.forEach(({ prefix, data, errors })=>{
        const subQueryGroupForPrefix = subQueryGroupsWithoutSkip.find((subQueryWithMetadata)=>subQueryWithMetadata.prefix === prefix);
        // This is expected to happen, as the prefixes contain all sub-queries, but we're only
        // interested in the ones that weren't skipped.
        if (typeof subQueryGroupForPrefix === 'undefined') {
            return;
        }
        const { subQuery, variables } = subQueryGroupForPrefix;
        const sanitizedErrors = sanitizeComposedErrors(errors);
        resultStore.setFetchingResults(subQuery, variables, {
            data,
            errors: sanitizedErrors
        });
    });
}

//# sourceMappingURL=useRootQuery.js.map